import { STAFF_ROLES, isStaffRole, type StaffRole } from "./auth-contract";

/**
 * Client mirror of the backend staff role policy
 * (`functions/src/staff-role-policy.ts`). Central UI gating must go through
 * this single capability map instead of hand-rolled role comparisons.
 *
 * The matrix below is duplicated on purpose so the client bundle never
 * imports from `functions/`; the contract test
 * (`src/lib/central-role-policy.test.mts`) imports both matrices and fails
 * when they drift.
 */

export const CENTRAL_CLIENT_STAFF_ROLES = STAFF_ROLES;

export const CENTRAL_CLIENT_STAFF_CAPABILITIES = [
  "case",
  "document",
  "financial",
  "settings",
  "approvals",
  "exports",
  "staff_administration",
  "mutations",
  "identity_support",
] as const;

export type CentralClientStaffCapability =
  (typeof CENTRAL_CLIENT_STAFF_CAPABILITIES)[number];

export const CENTRAL_CLIENT_ROLE_CAPABILITIES = {
  admin: [...CENTRAL_CLIENT_STAFF_CAPABILITIES],
  director: [
    "case",
    "document",
    "financial",
    "settings",
    "approvals",
    "exports",
    "mutations",
  ],
  arranger: ["case", "document", "mutations"],
  office_staff: ["case", "document", "exports", "mutations"],
  read_only: ["case", "document"],
  support: ["identity_support"],
  staff: ["case", "document", "exports", "mutations"],
} as const satisfies Record<
  StaffRole,
  readonly CentralClientStaffCapability[]
>;

export function isCentralClientStaffRole(value: unknown): value is StaffRole {
  return isStaffRole(value);
}

/**
 * Returns true when the given staff role carries the given capability.
 * Unknown roles (including `null`/`undefined` while auth is loading) and
 * unknown capabilities are denied.
 */
export function roleHasCapability(
  role: unknown,
  capability: unknown,
): boolean {
  if (!isStaffRole(role) || typeof capability !== "string") return false;
  return (CENTRAL_CLIENT_ROLE_CAPABILITIES[role] as readonly string[])
    .includes(capability);
}
